import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@src/environments/environment';
import { Observable, map } from 'rxjs';
import { ServicioSesion } from '@app/services/sesion/sesion.service';
import {
  CrmConfig,
  CrmConfigUsuario,
  CrmDashboardResponse,
  CrmLead,
  CrmLeadFiltros,
  CrmLeadsResponse,
  MetaPaginasResponse,
  CrmNota,
  CrmStats,
  CrmTarea,
  CrmTipoSeguimiento,
  CrmTipoTarea,
  CrmUsuarioDisponible,
} from '@app/models/crm/crm-leads.model';
import { CrmConfigCampoPersonalizado } from '@app/models/crm/crm-campo-personalizado.model';

@Injectable({ providedIn: 'root' })
export class CrmService {
  private _http = inject(HttpClient);
  private _url = environment.url;
  private _sesion = inject(ServicioSesion);

  private _headers(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${this._sesion.obtenerToken()}` });
  }

  // ── Leads ─────────────────────────────────────────────────────

  public getLeads(filtros: CrmLeadFiltros = {}): Observable<CrmLeadsResponse> {
    let params = new HttpParams();
    const add = (k: string, v: string | number | null | undefined) => {
      if (v != null && v !== '') params = params.set(k, String(v));
    };

    add('escuela_id',           filtros.escuela_id);
    add('liga_id',             filtros.liga_id);
    add('status',              filtros.status);
    add('per_page',            filtros.per_page);
    add('page',                filtros.page);
    add('order_by',            filtros.order_by);
    add('order_dir',           filtros.order_dir);
    add('fecha_desde',         filtros.fecha_desde);
    add('fecha_hasta',         filtros.fecha_hasta);
    add('campaign_name',       filtros.campaign_name);
    add('form_name',           filtros.form_name);
    add('lead_source',         filtros.lead_source);
    add('usuario_asignado_id', filtros.usuario_asignado_id);
    add('busqueda',            filtros.busqueda);
    if (filtros.solo_archivados) params = params.set('solo_archivados', '1');
    if (filtros.con_archivados)  params = params.set('con_archivados',  '1');
    for (const key in filtros.campo_filtros ?? {}) {
      params = params.set(`campo_filtros[${key}]`, filtros.campo_filtros![key]);
    }

    return this._http.get<CrmLeadsResponse>(`${this._url}/crm/leads`, {
      headers: this._headers(), params,
    });
  }

  public getLead(id: number): Observable<{ data: CrmLead }> {
    return this._http.get<{ data: CrmLead }>(`${this._url}/crm/leads/${id}`, {
      headers: this._headers(),
    });
  }

  public createLead(data: Partial<CrmLead>): Observable<{ message: string; data: CrmLead }> {
    return this._http.post<{ message: string; data: CrmLead }>(
      `${this._url}/crm/leads`, data, { headers: this._headers() },
    );
  }

  public updateLead(id: number, data: Partial<CrmLead>): Observable<{ message: string; data: CrmLead }> {
    return this._http.put<{ message: string; data: CrmLead }>(
      `${this._url}/crm/leads/${id}`, data, { headers: this._headers() },
    );
  }

  public updateLeadStatus(id: number, status: string): Observable<{ message: string; data: CrmLead }> {
    return this._http.patch<{ message: string; data: CrmLead }>(
      `${this._url}/crm/leads/${id}/status`, { status }, { headers: this._headers() },
    );
  }

  public archiveLead(id: number, archived: boolean): Observable<{ message: string; data: CrmLead }> {
    return this._http.patch<{ message: string; data: CrmLead }>(
      `${this._url}/crm/leads/${id}/archive`, { archived }, { headers: this._headers() },
    );
  }

  public deleteLead(id: number): Observable<{ message: string }> {
    return this._http.delete<{ message: string }>(`${this._url}/crm/leads/${id}`, {
      headers: this._headers(),
    });
  }

  public getStats(escuela_id?: number): Observable<CrmStats> {
    let params = new HttpParams();
    if (escuela_id != null) params = params.set('escuela_id', escuela_id);
    return this._http.get<CrmStats>(`${this._url}/crm/stats`, {
      headers: this._headers(), params,
    });
  }

  public getDashboardCrm(filtros: { escuela_id?: number; liga_id?: string } = {}, periodo?: { anio: number; mes: number }): Observable<CrmDashboardResponse> {
    let params = new HttpParams();
    if (filtros.escuela_id != null) params = params.set('escuela_id', filtros.escuela_id);
    else if (filtros.liga_id != null) params = params.set('liga_id', filtros.liga_id);
    if (periodo) {
      params = params.set('anio', periodo.anio).set('mes', periodo.mes);
    }
    return this._http.get<CrmDashboardResponse>(`${this._url}/dashboard/crm`, {
      headers: this._headers(), params,
    });
  }

  // ── Notas ─────────────────────────────────────────────────────

  public getNotas(leadId: number): Observable<CrmNota[]> {
    return this._http.get<CrmNota[]>(`${this._url}/crm/leads/${leadId}/notas`, {
      headers: this._headers(),
    });
  }

  public createNota(leadId: number, data: { usuario_id?: number | null; tipo_seguimiento_id?: number | null; nota: string }): Observable<{ message: string; data: CrmNota }> {
    return this._http.post<{ message: string; data: CrmNota }>(
      `${this._url}/crm/leads/${leadId}/notas`, data, { headers: this._headers() },
    );
  }

  public updateNota(notaId: number, data: { tipo_seguimiento_id?: number | null; nota: string }): Observable<{ message: string; data: CrmNota }> {
    return this._http.put<{ message: string; data: CrmNota }>(
      `${this._url}/crm/notas/${notaId}`, data, { headers: this._headers() },
    );
  }

  public deleteNota(notaId: number): Observable<{ message: string }> {
    return this._http.delete<{ message: string }>(`${this._url}/crm/notas/${notaId}`, {
      headers: this._headers(),
    });
  }

  // ── Tareas ────────────────────────────────────────────────────

  public getTareasGlobal(filtros: { usuario_asignado_id?: number; escuela_id?: number; liga_id?: string; status?: string; fecha_desde?: string; fecha_hasta?: string } = {}): Observable<CrmTarea[]> {
    let params = new HttpParams();
    if (filtros.usuario_asignado_id != null) params = params.set('usuario_asignado_id', filtros.usuario_asignado_id);
    if (filtros.escuela_id != null)           params = params.set('escuela_id', filtros.escuela_id);
    if (filtros.liga_id != null)             params = params.set('liga_id', filtros.liga_id);
    if (filtros.status)                      params = params.set('status', filtros.status);
    if (filtros.fecha_desde)                 params = params.set('fecha_desde', filtros.fecha_desde);
    if (filtros.fecha_hasta)                 params = params.set('fecha_hasta', filtros.fecha_hasta);
    return this._http.get<CrmTarea[]>(`${this._url}/crm/tareas`, {
      headers: this._headers(), params,
    });
  }

  public getTareas(leadId: number, status?: string): Observable<CrmTarea[]> {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    return this._http.get<CrmTarea[]>(`${this._url}/crm/leads/${leadId}/tareas`, {
      headers: this._headers(), params,
    });
  }

  public createTarea(leadId: number, data: { usuario_id?: number | null; tipo_tarea_id?: number | null; descripcion: string; usuario_asignado_id?: number | null; status?: string; fecha_hora?: string | null }): Observable<{ message: string; data: CrmTarea }> {
    return this._http.post<{ message: string; data: CrmTarea }>(
      `${this._url}/crm/leads/${leadId}/tareas`, data, { headers: this._headers() },
    );
  }

  public updateTarea(tareaId: number, data: Partial<{ tipo_tarea_id: number | null; descripcion: string; usuario_asignado_id: number | null; status: string; fecha_hora: string | null }>): Observable<{ message: string; data: CrmTarea }> {
    return this._http.put<{ message: string; data: CrmTarea }>(
      `${this._url}/crm/tareas/${tareaId}`, data, { headers: this._headers() },
    );
  }

  public deleteTarea(tareaId: number): Observable<{ message: string }> {
    return this._http.delete<{ message: string }>(`${this._url}/crm/tareas/${tareaId}`, {
      headers: this._headers(),
    });
  }

  // ── Tipos de Seguimiento ──────────────────────────────────────

  public getTiposSeguimiento(): Observable<CrmTipoSeguimiento[]> {
    return this._http.get<CrmTipoSeguimiento[]>(`${this._url}/crm/tipo-seguimiento`, {
      headers: this._headers(),
    });
  }

  public createTipoSeguimiento(data: { nombre: string; descripcion?: string }): Observable<{ message: string; data: CrmTipoSeguimiento }> {
    return this._http.post<{ message: string; data: CrmTipoSeguimiento }>(
      `${this._url}/crm/tipo-seguimiento`, data, { headers: this._headers() },
    );
  }

  public updateTipoSeguimiento(id: number, data: { nombre: string; descripcion?: string }): Observable<{ message: string; data: CrmTipoSeguimiento }> {
    return this._http.put<{ message: string; data: CrmTipoSeguimiento }>(
      `${this._url}/crm/tipo-seguimiento/${id}`, data, { headers: this._headers() },
    );
  }

  public deleteTipoSeguimiento(id: number): Observable<{ message: string }> {
    return this._http.delete<{ message: string }>(`${this._url}/crm/tipo-seguimiento/${id}`, {
      headers: this._headers(),
    });
  }

  // ── Tipos de Tarea ────────────────────────────────────────────

  public getTiposTarea(): Observable<CrmTipoTarea[]> {
    return this._http.get<CrmTipoTarea[]>(`${this._url}/crm/tipo-tareas`, {
      headers: this._headers(),
    });
  }

  public createTipoTarea(data: { nombre: string; descripcion?: string }): Observable<{ message: string; data: CrmTipoTarea }> {
    return this._http.post<{ message: string; data: CrmTipoTarea }>(
      `${this._url}/crm/tipo-tareas`, data, { headers: this._headers() },
    );
  }

  public updateTipoTarea(id: number, data: { nombre: string; descripcion?: string }): Observable<{ message: string; data: CrmTipoTarea }> {
    return this._http.put<{ message: string; data: CrmTipoTarea }>(
      `${this._url}/crm/tipo-tareas/${id}`, data, { headers: this._headers() },
    );
  }

  public deleteTipoTarea(id: number): Observable<{ message: string }> {
    return this._http.delete<{ message: string }>(`${this._url}/crm/tipo-tareas/${id}`, {
      headers: this._headers(),
    });
  }

  // ── Configuración CRM ─────────────────────────────────────────

  public getConfigs(filtros: { escuela_id?: number; liga_id?: string } = {}): Observable<CrmConfig[]> {
    let params = new HttpParams();
    if (filtros.escuela_id != null) params = params.set('escuela_id', filtros.escuela_id);
    if (filtros.liga_id != null)    params = params.set('liga_id',    filtros.liga_id);
    return this._http.get<CrmConfig[]>(`${this._url}/crm/configs`, {
      headers: this._headers(), params,
    });
  }

  public tieneConfig(filtros: { escuela_id?: number; liga_id?: string } = {}): Observable<boolean> {
    return this.getConfigs(filtros).pipe(map(cs => cs.length > 0));
  }

  public getConfig(id: number): Observable<{ data: CrmConfig }> {
    return this._http.get<{ data: CrmConfig }>(`${this._url}/crm/configs/${id}`, {
      headers: this._headers(),
    });
  }

  public createConfig(data: Partial<CrmConfig>): Observable<{ message: string; data: CrmConfig }> {
    return this._http.post<{ message: string; data: CrmConfig }>(
      `${this._url}/crm/configs`, data, { headers: this._headers() },
    );
  }

  public updateConfig(id: number, data: Partial<CrmConfig>): Observable<{ message: string; data: CrmConfig }> {
    return this._http.put<{ message: string; data: CrmConfig }>(
      `${this._url}/crm/configs/${id}`, data, { headers: this._headers() },
    );
  }

  public deleteConfig(id: number): Observable<{ message: string }> {
    return this._http.delete<{ message: string }>(`${this._url}/crm/configs/${id}`, {
      headers: this._headers(),
    });
  }

  // ── Usuarios de Configuración ─────────────────────────────────

  public getConfigUsuarios(configId: number): Observable<CrmConfigUsuario[]> {
    return this._http.get<CrmConfigUsuario[]>(`${this._url}/crm/configs/${configId}/usuarios`, {
      headers: this._headers(),
    });
  }

  public addConfigUsuario(configId: number, usuario_id: number): Observable<{ message: string; data: CrmConfigUsuario }> {
    return this._http.post<{ message: string; data: CrmConfigUsuario }>(
      `${this._url}/crm/configs/${configId}/usuarios`, { usuario_id }, { headers: this._headers() },
    );
  }

  public removeConfigUsuario(configId: number, crmUserId: number): Observable<{ message: string }> {
    return this._http.delete<{ message: string }>(
      `${this._url}/crm/configs/${configId}/usuarios/${crmUserId}`, { headers: this._headers() },
    );
  }

  public getUsuariosDisponibles(configId: number): Observable<CrmUsuarioDisponible[]> {
    return this._http.get<CrmUsuarioDisponible[]>(
      `${this._url}/crm/configs/${configId}/usuarios-disponibles`, { headers: this._headers() },
    );
  }

  // ── Campos personalizados ──────────────────────────────────────

  public getCamposPersonalizados(configId: number): Observable<{ data: CrmConfigCampoPersonalizado[]; total: number }> {
    return this._http.get<{ data: CrmConfigCampoPersonalizado[]; total: number }>(
      `${this._url}/crm/configs/${configId}/campos-personalizados`, { headers: this._headers() },
    );
  }

  public createCampoPersonalizado(configId: number, data: Partial<CrmConfigCampoPersonalizado>): Observable<{ message: string; data: CrmConfigCampoPersonalizado }> {
    return this._http.post<{ message: string; data: CrmConfigCampoPersonalizado }>(
      `${this._url}/crm/configs/${configId}/campos-personalizados`, data, { headers: this._headers() },
    );
  }

  public updateCampoPersonalizado(configId: number, campoId: number, data: Partial<CrmConfigCampoPersonalizado>): Observable<{ message: string; data: CrmConfigCampoPersonalizado }> {
    return this._http.put<{ message: string; data: CrmConfigCampoPersonalizado }>(
      `${this._url}/crm/configs/${configId}/campos-personalizados/${campoId}`, data, { headers: this._headers() },
    );
  }

  public deleteCampoPersonalizado(configId: number, campoId: number): Observable<{ message: string }> {
    return this._http.delete<{ message: string }>(
      `${this._url}/crm/configs/${configId}/campos-personalizados/${campoId}`, { headers: this._headers() },
    );
  }

  // ── Conexión con Meta ────────────────────────────────────────────

  public getMetaPaginas(configId: number, accessToken: string): Observable<MetaPaginasResponse> {
    return this._http.post<MetaPaginasResponse>(
      `${this._url}/crm/configs/${configId}/meta/paginas`, { access_token: accessToken }, { headers: this._headers() },
    );
  }

  public vincularMetaPagina(configId: number, sessionId: string, pageId: string): Observable<{ message: string; data: CrmConfig }> {
    return this._http.post<{ message: string; data: CrmConfig }>(
      `${this._url}/crm/configs/${configId}/meta/vincular`,
      { session_id: sessionId, page_id: pageId },
      { headers: this._headers() },
    );
  }
}
